import React from 'react';

interface RegionFlagProps {
  /** Single region code, e.g. 'fi'. Unknown codes render nothing. */
  region: string;
  className?: string;
}

interface RegionFlagsProps {
  /** Comma-separated region codes, e.g. 'se,no,dk,fr'. */
  regions?: string;
  className?: string;
  flagClassName?: string;
}

// Twelve stars evenly spaced on a circle, per the European flag. Drawn as dots rather than
// five-pointed stars because at this size the points collapse into noise.
const EU_STARS = [
  [9, 3], [10.5, 3.4], [11.6, 4.5], [12, 6], [11.6, 7.5], [10.5, 8.6],
  [9, 9], [7.5, 8.6], [6.4, 7.5], [6, 6], [6.4, 4.5], [7.5, 3.4]
];

/**
 * Nordic cross flags share a geometry, so they differ only in colours and cross weight.
 * Every flag is drawn in a common 18x12 viewBox rather than its official aspect ratio: a row of
 * flags at mixed ratios reads as ragged, and the small proportional inaccuracy is invisible at
 * this size while the alignment gain is not.
 */
const nordicCross = (background: string, cross: string) => (
  <>
    <rect width="18" height="12" fill={background} />
    <rect y="4.5" width="18" height="3" fill={cross} />
    <rect x="5" width="3" height="12" fill={cross} />
  </>
);

const FLAGS: Record<string, { label: string; content: React.ReactNode }> = {
  fi: { label: 'Finland', content: nordicCross('#ffffff', '#002f6c') },
  se: { label: 'Sweden', content: nordicCross('#006aa7', '#fecc00') },
  dk: { label: 'Denmark', content: nordicCross('#c8102e', '#ffffff') },
  no: {
    label: 'Norway',
    content: (
      <>
        <rect width="18" height="12" fill="#ba0c2f" />
        <rect y="4" width="18" height="4" fill="#ffffff" />
        <rect x="4" width="4" height="12" fill="#ffffff" />
        <rect y="5" width="18" height="2" fill="#00205b" />
        <rect x="5" width="2" height="12" fill="#00205b" />
      </>
    )
  },
  fr: {
    label: 'France',
    content: (
      <>
        <rect width="6" height="12" fill="#002395" />
        <rect x="6" width="6" height="12" fill="#ffffff" />
        <rect x="12" width="6" height="12" fill="#ed2939" />
      </>
    )
  },
  eu: {
    label: 'European Union',
    content: (
      <>
        <rect width="18" height="12" fill="#003399" />
        {EU_STARS.map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="0.6" fill="#ffcc00" />
        ))}
      </>
    )
  }
};

/**
 * Small flag indicating a region an app serves.
 *
 * Deliberately inline SVG rather than flag emoji: Windows has no colour flag glyphs, so an emoji
 * flag renders there as bare letters ("FI") and the visual cue is lost for a large share of
 * desktop users.
 *
 * Decorative - the button's own text names the region, so these are hidden from screen readers
 * rather than read out as a list of country names.
 */
export const RegionFlag: React.FC<RegionFlagProps> = ({ region, className = '' }) => {
  const flag = FLAGS[region.trim().toLowerCase()];

  if (!flag) {
    return null;
  }

  return (
    <svg className={className} viewBox="0 0 18 12" aria-hidden="true" focusable="false">
      <title>{flag.label}</title>
      {flag.content}
    </svg>
  );
};

/**
 * Row of flags for a comma-separated list of region codes. Unknown codes are skipped, so adding a
 * market is a config change plus one entry in FLAGS.
 */
export const RegionFlags: React.FC<RegionFlagsProps> = ({ regions, className = '', flagClassName = '' }) => {
  const codes = (regions ?? '')
    .split(',')
    .map(code => code.trim())
    .filter(code => code.length > 0 && FLAGS[code.toLowerCase()]);

  if (codes.length === 0) {
    return null;
  }

  return (
    <span className={className} aria-hidden="true">
      {codes.map(code => (
        <RegionFlag key={code} region={code} className={flagClassName} />
      ))}
    </span>
  );
};
